/**
 * QbMaintenanceModal
 *
 * Console-style modal for running allowlisted QuickBox CLI operations.
 * Shows live streaming output via SSE with auto-scroll, copy, and line-wrap toggle.
 *
 * Resilience strategy:
 *   1. Primary: SSE stream via EventSource for live output + terminal status event
 *   2. Relay: If primary SSE disconnects (e.g. during update_quickbox service restart),
 *      automatically switches to the update-relay sidecar at /api/admin/qb/relay/stream.
 *      Health-polls /health every 2s; reconnects to primary once v4 is back.
 *   3. Fallback: If both SSE sources fail, polls GET /api/admin/qb/status until terminal
 *   4. Terminal states: "completed" or "failed" — always fires onComplete for refresh
 *
 * Running view state machine (update_quickbox only):
 *   running:progress  — progress bar showing dashboard update phase (steps 1–8)
 *   running:console   — text console for post-dashboard quickbox steps
 *   (other actions go straight to running:console as before)
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { cn } from "../../utils/cn";
import { useConsoleScroll } from "../../hooks/useConsoleScroll";
import { useJobStream } from "../../hooks/useJobStream";
import { ConsolePanel } from "../system/ConsolePanel";
import { CancelJobButton } from "../jobs/CancelJobButton";
import { PlexCredentialModal, type PlexCredentials } from "../packages/PlexCredentialModal";
import { PlexChannelSelectorModal } from "../packages/PlexChannelSelectorModal";

type QbAction = "fix_version" | "update_quickbox" | "update_emby" | "update_jellyfin" | "update_plex";
type JobStatus = "idle" | "confirming" | "running" | "completed" | "failed" | "aborted";

/**
 * Internal running sub-state to drive the progress-bar vs console-panel split.
 * Only applies when status === "running".
 */
type RunningView = "progress" | "console";

type QbMaintenanceModalProps = {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
  initialAction?: QbAction;
  /** When true, skip the action picker and run the initial action immediately */
  skipConfirmation?: boolean;
  /** Use beta/unstable channel for Emby/Jellyfin updates */
  beta?: boolean;
};

const ACTION_CONFIG: Record<QbAction, { label: string; description: string; icon: string }> = {
  fix_version: {
    label: "Fix version",
    description: "Runs 'qb fix version' to repair version tracking and dependencies.",
    icon: "lucide:wrench",
  },
  update_quickbox: {
    label: "Update QuickBox",
    description: "Runs 'qb update quickbox' to update QuickBox to the latest version.",
    icon: "lucide:refresh-cw",
  },
  update_emby: {
    label: "Update Emby Server",
    description: "Runs 'qb update emby' to update the Emby media server to the latest version.",
    icon: "simple-icons:emby",
  },
  update_jellyfin: {
    label: "Update Jellyfin Server",
    description: "Runs 'qb update jellyfin' to update the Jellyfin media server to the latest version.",
    icon: "simple-icons:jellyfin",
  },
  update_plex: {
    label: "Update Plex Server",
    description: "Runs 'qb update plex' to update the Plex media server to the latest version.",
    icon: "simple-icons:plex",
  },
};

const STATUS_BADGES: Record<JobStatus, { label: string; className: string }> = {
  idle: { label: "Idle", className: "bg-muted text-muted-foreground" },
  confirming: { label: "Confirming", className: "bg-yellow-500/20 text-yellow-400" },
  running: { label: "Running", className: "bg-blue-500/20 text-blue-400 animate-pulse" },
  completed: { label: "Completed", className: "bg-emerald-500/20 text-emerald-400" },
  failed: { label: "Failed", className: "bg-red-500/20 text-red-400" },
  aborted: { label: "Cancelled", className: "bg-orange-500/20 text-orange-400" },
};

export const QbMaintenanceModal = ({ open, onClose, onComplete, initialAction, skipConfirmation, beta: betaProp }: QbMaintenanceModalProps) => {
  const [status, setStatus] = useState<JobStatus>("confirming");
  const [action, setAction] = useState<QbAction>(initialAction ?? "fix_version");
  const [betaToggle, setBetaToggle] = useState(betaProp ?? false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [plexGateOpen, setPlexGateOpen] = useState(false);
  // Plex channel selector — opened when autodetect is uncertain (detectedChannel: "unknown")
  const [plexChannelSelectorOpen, setPlexChannelSelectorOpen] = useState(false);

  /**
   * Running view: drives progress-bar vs console-panel during a job.
   * "progress" until step 8 at 100% + 250ms, then "console".
   * Non-update_quickbox actions start in "console" immediately.
   */
  const [runningView, setRunningView] = useState<RunningView>("console");

  /**
   * Whether the progress-bar→console transition animation is in progress.
   * During transition, progress bar fades to 50% opacity over 250ms.
   */
  const [transitioning, setTransitioning] = useState(false);

  /**
   * Timestamp (ms) when step 8 reached 100%. Used for the 500ms fast-path:
   * if event:complete fires within 500ms of this, skip console mode entirely.
   */
  const step8CompletedAtRef = useRef<number | null>(null);

  /**
   * Timeout ref for the progress→console transition delay.
   */
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onCompleteRef = useRef(onComplete);
  const autoStartedRef = useRef(false);
  onCompleteRef.current = onComplete;
  const titleId = useId();

  // Relay URLs are only set after a successful job start (jobId known).
  // update_quickbox is the only action that takes v4 down, but we wire relay
  // for all actions — it's a no-op when the primary stream stays healthy.
  const relayStreamUrl = jobId ? `/api/admin/qb/relay/stream?jobId=${jobId}` : undefined;
  const relayStatusUrl = jobId ? `/api/admin/qb/relay/status?jobId=${jobId}` : undefined;

  const stream = useJobStream({
    streamUrl: "/api/admin/qb/stream",
    statusUrl: "/api/admin/qb/status",
    relayStreamUrl,
    relayStatusUrl,
  });

  const scroll = useConsoleScroll(stream.lines.length);

  // Format elapsed seconds as M:SS or H:MM:SS
  const elapsedLabel = (() => {
    const s = stream.elapsedSec;
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const sec = s % 60;
    if (m < 60) return `${m}:${String(sec).padStart(2, "0")}`;
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${h}:${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  })();

  const lastActivityLabel = (() => {
    if (stream.lastActivitySec === null) return null;
    const s = stream.lastActivitySec;
    if (s < 2) return "just now";
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    return `${m}m ago`;
  })();

  // After 10s of silence, show a soft reassurance note instead of letting the
  // user think the operation hung. Not an error — just a status update.
  const showSilenceNote = status === "running" && (stream.lastActivitySec ?? 0) > 10;

  // Sync terminal status from stream
  useEffect(() => {
    if (stream.isTerminal && status === "running") {
      // If event:complete fires within 500ms of step 8 reaching 100%, skip console mode.
      // Clear any pending transition timer so we don't flip to console after terminal.
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
        transitionTimerRef.current = null;
      }
      setTransitioning(false);
      setStatus(stream.status as "completed" | "failed" | "aborted");
      onCompleteRef.current?.();
    }
  }, [stream.isTerminal, stream.status, status]);

  // Watch for step=8 at 100% to trigger the progress→console transition
  useEffect(() => {
    if (status !== "running" || runningView !== "progress") return;
    if (!stream.progress) return;

    const { step, total, percent } = stream.progress;
    const isFinalStep = step >= total;
    const isComplete = percent >= 100;

    if (isFinalStep && isComplete) {
      if (step8CompletedAtRef.current === null) {
        step8CompletedAtRef.current = Date.now();
      }

      // Start the fade-out transition
      setTransitioning(true);

      // After 250ms fade, flip to console — unless event:complete already fired
      transitionTimerRef.current = setTimeout(() => {
        setTransitioning(false);
        setRunningView("console");
      }, 250);
    }
  }, [stream.progress, status, runningView]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStatus(skipConfirmation ? "running" : "confirming");
      setAction(initialAction ?? "fix_version");
      setBetaToggle(betaProp ?? false);
      setJobId(null);
      setPlexGateOpen(false);
      setPlexChannelSelectorOpen(false);
      setRunningView("console");
      setTransitioning(false);
      step8CompletedAtRef.current = null;
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
        transitionTimerRef.current = null;
      }
      stream.reset();
    }
  }, [open, initialAction, skipConfirmation, betaProp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup transition timer on unmount
  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
      }
    };
  }, []);

  const startJob = useCallback(async (plexCredentials?: PlexCredentials, plexChannel?: "free" | "plexpass") => {
    setStatus("running");
    setJobId(null);

    // Console view for every action.  The earlier progress-bar branch for
    // update_quickbox produced a 0% bar that never advanced because not all
    // streaming sources emit [PROGRESS] markers (e.g. the v4 admin endpoint's
    // own journal-follow path), and hid the live text behind a useless UI.
    // Console mode is the consistent, debuggable surface across all actions.
    setRunningView("console");
    setTransitioning(false);
    step8CompletedAtRef.current = null;

    stream.reset();

    try {
      const body: Record<string, unknown> = { action };
      if ((action === "update_emby" || action === "update_jellyfin") && betaToggle) {
        body.beta = true;
      }
      if (plexChannel) {
        body.plexChannel = plexChannel;
      }
      if (plexCredentials) {
        body.plexEmail = plexCredentials.plexEmail;
        body.plexPassword = plexCredentials.plexPassword;
        body.plexOtp = plexCredentials.plexOtp;
      }

      const res = await fetch("/api/admin/qb/run", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      const data = await res.json() as Record<string, unknown>;

      if (!res.ok || data.ok !== true) {
        stream.reset();
        setStatus("failed");
        return;
      }

      const responseData = data.data as Record<string, unknown> | undefined;

      // Autodetect: channel unknown — user must pick Free or PlexPass
      if (responseData?.needsChannelSelection === true) {
        stream.reset();
        setStatus("confirming");
        setPlexChannelSelectorOpen(true);
        return;
      }

      // Autodetect: PlexPass detected (or plexpass sent without creds) — collect credentials
      if (responseData?.credentialsRequired === true) {
        stream.reset();
        setStatus("confirming");
        setPlexGateOpen(true);
        return;
      }

      // Job started — capture jobId for relay fallback URL construction
      if (typeof data.jobId === "string") {
        setJobId(data.jobId);
      }

      stream.connect();
    } catch {
      stream.reset();
      setStatus("failed");
    }
  }, [action, betaToggle, stream]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-start when skipConfirmation is set
  useEffect(() => {
    if (open && skipConfirmation && status === "running" && !autoStartedRef.current) {
      autoStartedRef.current = true;
      void startJob();
    }
    if (!open) {
      autoStartedRef.current = false;
    }
  }, [open, skipConfirmation, status, startJob]);

  const handleClose = useCallback(() => {
    setPlexGateOpen(false);
    setPlexChannelSelectorOpen(false);
    stream.disconnect();
    onClose();
  }, [onClose, stream]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAborted = useCallback((_status: string) => {
    stream.disconnect();
    setStatus("aborted");
    onCompleteRef.current?.();
  }, [stream]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Called when PlexCredentialModal is dismissed without submitting. */
  const handlePlexCredentialClose = useCallback(() => {
    setPlexGateOpen(false);
    // Return to confirming state so user can try again or cancel
    setStatus("confirming");
  }, []);

  /** Called when PlexCredentialModal submits credentials. Always sends plexChannel: "plexpass". */
  const handlePlexCredentialSubmit = useCallback(async (creds: PlexCredentials) => {
    setPlexGateOpen(false);
    await startJob(creds, "plexpass");
  }, [startJob]);

  /**
   * Called when channel selector picks Free.
   * Resubmits startJob with plexChannel: "free" — no credentials needed.
   */
  const handlePlexChannelFree = useCallback(async () => {
    setPlexChannelSelectorOpen(false);
    await startJob(undefined, "free");
  }, [startJob]);

  /**
   * Called when channel selector picks PlexPass.
   * Skips the roundtrip — opens PlexCredentialModal directly.
   * handlePlexCredentialSubmit will send plexChannel: "plexpass" with creds.
   */
  const handlePlexChannelPlexPass = useCallback(() => {
    setPlexChannelSelectorOpen(false);
    setPlexGateOpen(true);
  }, []);

  if (!open) return null;

  const badge = STATUS_BADGES[status];
  const isTerminal = status === "completed" || status === "failed" || status === "aborted";
  const actionConfig = ACTION_CONFIG[action];

  // Progress bar derived values
  const progressPercent = stream.progress?.percent ?? 0;
  const progressStep = stream.progress?.step ?? 0;
  const progressTotal = stream.progress?.total ?? 8;
  const progressLabel = stream.progress?.label ?? null;

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in-0 duration-200"
        onClick={isTerminal || status === "confirming" ? handleClose : undefined}
      />
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="relative w-full max-w-3xl mx-4 animate-in fade-in-0 zoom-in-95 duration-200">
        <div className="rounded-xl border border-white/[0.08] bg-card shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.04]">
                <Icon icon="lucide:terminal" className="h-4 w-4 text-foreground" />
              </div>
              <div>
                <h2 id={titleId} className="text-sm font-semibold text-foreground">System Response</h2>
                <p className="text-[10px] text-muted-foreground/70">QuickBox Maintenance</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {status === "running" && jobId && (
                <CancelJobButton
                  jobScope="update"
                  jobId={jobId}
                  isActive={status === "running"}
                  onAborted={handleAborted}
                />
              )}
              <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium", badge.className)}>
                {badge.label}
              </span>
              <button
                type="button"
                onClick={handleClose}
                disabled={status === "running"}
                aria-label="Close dialog"
                className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/[0.06] transition-colors disabled:opacity-40"
              >
                <Icon icon="lucide:x" className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Confirmation view */}
          {status === "confirming" && (
            <div className="px-5 py-6 space-y-4">
              <p className="text-sm text-foreground">Select a maintenance operation:</p>
              <div className="space-y-2">
                {(["fix_version", "update_quickbox"] as const).map((a) => {
                  const config = ACTION_CONFIG[a];
                  return (
                    <label
                      key={a}
                      className={cn(
                        "flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                        action === a
                          ? "border-blue-500/50 bg-blue-500/5"
                          : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
                      )}
                    >
                      <input
                        type="radio"
                        name="qb-action"
                        value={a}
                        checked={action === a}
                        onChange={() => setAction(a)}
                        className="accent-blue-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon icon={config.icon} className="h-4 w-4 text-muted-foreground" />
                          <p className="text-sm font-medium text-foreground">{config.label}</p>
                        </div>
                        <p className="text-[10px] text-muted-foreground/70 mt-0.5">{config.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
              {action === "update_quickbox" && (
                <div className="flex items-start gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-3 py-2">
                  <Icon icon="lucide:alert-triangle" className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-yellow-200/90">
                    Updating QuickBox may take several minutes and could require service restarts.
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" size="sm" onClick={handleClose}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" onClick={() => void startJob()}>
                  <Icon icon={actionConfig.icon} className="h-3.5 w-3.5 mr-1.5" />
                  {actionConfig.label}
                </Button>
              </div>
            </div>
          )}

          {/* Running: progress bar view (update_quickbox dashboard phase only) */}
          {status !== "confirming" && !isTerminal && runningView === "progress" && (
            <div
              className={cn(
                "px-5 py-6 space-y-4 transition-opacity duration-250",
                transitioning ? "opacity-50" : "opacity-100"
              )}
            >
              {/* Phase label row */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-foreground">
                    {progressLabel ?? "Preparing update…"}
                  </span>
                  <span className="text-[10px] tabular-nums text-muted-foreground/60">
                    {progressPercent}%
                  </span>
                </div>

                {/* Progress track */}
                <div
                  className="h-1.5 w-full rounded-full overflow-hidden bg-muted/30"
                  role="progressbar"
                  aria-valuenow={progressPercent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={progressLabel ?? "Update progress"}
                >
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>

                {/* Step counter */}
                {progressStep > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground/60">
                      Step {progressStep} of {progressTotal}
                    </span>
                  </div>
                )}
              </div>

              {/* Elapsed timer — same visual style as existing running afterLines */}
              <div className="flex flex-col gap-1 font-sans text-[10px] text-muted-foreground/60">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse flex-shrink-0" />
                  <span>
                    Elapsed: <span className="tabular-nums font-medium text-muted-foreground">{elapsedLabel}</span>
                    {lastActivityLabel && (
                      <span className="ml-2">· last activity <span className="tabular-nums font-medium text-muted-foreground">{lastActivityLabel}</span></span>
                    )}
                  </span>
                </div>
                {showSilenceNote && (
                  <div className="ml-3.5 text-muted-foreground/50">
                    Still working — long-running step in progress
                  </div>
                )}
              </div>

              {stream.error && (
                <div className="text-xs text-yellow-400/80">{stream.error}</div>
              )}
            </div>
          )}

          {/* Running: console view (post-dashboard steps or non-QB-update actions) */}
          {status !== "confirming" && (runningView === "console" || isTerminal) && (
            <>
              <ConsolePanel
                lines={stream.lines}
                consoleRef={scroll.consoleRef}
                onScroll={scroll.handleScroll}
                wrapLines={scroll.wrapLines}
                onToggleWrap={() => scroll.setWrapLines((prev) => !prev)}
                autoScroll={scroll.autoScroll}
                onScrollToBottom={scroll.scrollToBottom}
                onCopy={() => scroll.handleCopyOutput(stream.lines)}
                durationMs={stream.durationMs}
                exitCode={stream.exitCode}
                isLoading={status === "running"}
                error={stream.error}
                afterLines={
                  status === "running" ? (
                    <div className="mt-3 flex flex-col gap-1 font-sans text-[10px] text-muted-foreground/60">
                      <div className="flex items-center gap-2">
                        {/* Pulsing dot — always visible while running */}
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse flex-shrink-0" />
                        <span>
                          Elapsed: <span className="tabular-nums font-medium text-muted-foreground">{elapsedLabel}</span>
                          {lastActivityLabel && (
                            <span className="ml-2">· last activity <span className="tabular-nums font-medium text-muted-foreground">{lastActivityLabel}</span></span>
                          )}
                        </span>
                      </div>
                      {showSilenceNote && (
                        <div className="ml-3.5 text-muted-foreground/50">
                          Still working — long-running step in progress
                        </div>
                      )}
                    </div>
                  ) : null
                }
              />

              {isTerminal && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06]">
                  <div className="text-xs text-muted-foreground/70">
                    {status === "completed"
                      ? "Update complete."
                      : status === "aborted"
                        ? "Job cancelled."
                        : `Update failed${stream.exitCode !== null ? ` (exit code ${stream.exitCode})` : ""}.`}
                  </div>
                  <Button variant="secondary" size="sm" onClick={handleClose}>
                    Close
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modal, document.body)}
      {/* Plex channel selector — opened when autodetect is uncertain */}
      <PlexChannelSelectorModal
        open={plexChannelSelectorOpen}
        onSelectFree={() => { void handlePlexChannelFree(); }}
        onSelectPlexPass={handlePlexChannelPlexPass}
        onClose={() => {
          setPlexChannelSelectorOpen(false);
          setStatus("confirming");
        }}
      />
      {/* Plex credential gate — opened when PlexPass is detected or selected */}
      <PlexCredentialModal
        open={plexGateOpen}
        onSubmit={handlePlexCredentialSubmit}
        onClose={handlePlexCredentialClose}
      />
    </>
  );
};
